import { useEffect } from 'react';
import { useTeamsStore } from '../store/teamsStore';
import { useTeamVaultStore, loadTeamVault } from '../store/teamVaultStore';

/**
 * Load every team's authorized vault view once, and keep it available.
 *
 * Two surfaces need this — the FTP sidebar (team SFTP rows) and the New Item
 * modal (its destination list) — and neither should own the fetch, because
 * whichever mounted second would either refetch or silently show nothing.
 *
 * A team is fetched exactly once per session unless something invalidates it:
 * the guard is the presence of an entry in `byTeam`, not a boolean, so a team
 * dropped by `forgetTeam` (left the team, lost every grant) is re-fetched if
 * the user is still a member. Realtime keeps entries fresh after that —
 * `teamVaultStore` subscribes to `team_vault_delta` at module load, so no
 * component has to be mounted for a change to land.
 *
 * Returns the RAW store slices. Both are stable references, so they are safe to
 * subscribe to directly; anything DERIVED from them must go through `useMemo`
 * at the call site (a selector that builds a fresh array on every call makes
 * `useSyncExternalStore` see a new snapshot every render and React aborts the
 * panel with "Maximum update depth exceeded" — this codebase has shipped that
 * exact bug once already).
 */
export function useTeamVaults() {
  const teams = useTeamsStore((s) => s.teams);
  const byTeam = useTeamVaultStore((s) => s.byTeam);

  useEffect(() => {
    for (const team of Array.isArray(teams) ? teams : []) {
      if (team?.id == null) continue;
      // Read through getState() rather than the subscribed `byTeam` so this
      // effect does not have to re-run (and re-decide) every time any team's
      // entry changes.
      if (!useTeamVaultStore.getState().byTeam[String(team.id)]) {
        loadTeamVault(team.id);
      }
    }
  }, [teams]);

  return { teams: Array.isArray(teams) ? teams : [], byTeam };
}

export default useTeamVaults;
